import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGetConfigurationQuery } from '../navigation/apiSlice';
import { accountSelector } from '../store/shared/accountSlice';

export interface PermissionsHook {
  /**
   * @returns true if permissions are loading, false otherwise
   */
  isLoading: boolean;
  /**
   *
   * @param permissions array of permissions to check for (e.g. [PERMISSIONS.ViewDeal])
   * @returns true if has access, false otherwise
   */
  hasAccess: ({ permissions }: { permissions: number[] }) => boolean;

  /**
   * @returns all permissions for the user
   */
  getPermissions: () => {
    accountPermissions: number[];
  };

  /**
   * @returns true if accountPermission is present and not empty else false
   */
  hasAccountPermissions: () => boolean;

  /**
   * refetch the permissions
   */
  refetch: () => void;

  /**
   * @returns the agreement IDs that this user is restricted from removing products from
   */
  restrictedAgreementIds: string[];
}

const usePermissions = (): PermissionsHook => {
  const { currentAccountId } = useSelector(accountSelector);
  const {
    data: userPermissions,
    isSuccess,
    refetch,
  } = useGetConfigurationQuery({
    type: 'admin',
    accountId: currentAccountId,
  });

  const hasAccess = useCallback(
    ({ permissions }: { permissions: number[] }): boolean => {
      if (!isSuccess) {
        return false;
      }

      return permissions.every((x) =>
        userPermissions.accountPermissions?.includes(x),
      );
    },
    [userPermissions, isSuccess],
  );

  const getPermissions = useCallback(
    () => ({
      accountPermissions: userPermissions?.accountPermissions ?? [],
    }),
    [userPermissions],
  );

  const hasAccountPermissions = () =>
    userPermissions?.accountPermissions &&
    userPermissions.accountPermissions.length > 0;

  return {
    isLoading: !isSuccess,
    hasAccess,
    getPermissions,
    hasAccountPermissions,
    refetch,
    restrictedAgreementIds: userPermissions?.restrictedAgreementIds ?? [],
  };
};

export default usePermissions;
